import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TaskStatus, TaskType } from 'types/api/collaborate';
import { RootState } from '../store.ts';

export type PageFilterType = 'current' | 'all' | 'assigned';

interface FiltersState {
  applyFilter?: boolean;
  pageFilter: PageFilterType;
  limit?: number;
  notifiedUsers?: string;
  offset?: number;
  orderBy?: 'created_at' | 'task_priority' | 'is_unread' | 'task_status';
  taskStatus?: TaskStatus | TaskStatus[];
  taskTypes?: TaskType | TaskType[];
  textSearch?: string;
}

const initialState = (): FiltersState => {
  try {
    const localStorage = window.localStorage.getItem('atarim-filters');
    const decodedFilters = JSON.parse(localStorage ?? '{}');
    return {
      pageFilter: decodedFilters.pageFilter || 'current',
      notifiedUsers: decodedFilters.notifiedUsers,
      orderBy: decodedFilters.orderBy,
      taskStatus: decodedFilters.taskStatus || [
        'in-progress',
        'open',
        'pending-review',
      ],
      taskTypes: decodedFilters.taskTypes,
      textSearch: decodedFilters.textSearch,
    };
  } catch {
    return {
      pageFilter: 'current',
      notifiedUsers: undefined,
      orderBy: undefined,
      taskStatus: ['in-progress', 'open', 'pending-review'],
      taskTypes: undefined,
      textSearch: undefined,
    };
  }
};

const updateFilterState = (data: Record<string, unknown>) => {
  try {
    const localStorage = window.localStorage.getItem('atarim-filters');
    const decodedFilters = JSON.parse(localStorage ?? '{}');
    window.localStorage.setItem(
      'atarim-filters',
      JSON.stringify({
        ...decodedFilters,
        ...data,
      })
    );
  } catch {
    window.localStorage.setItem('atarim-filters', JSON.stringify(data));
  }
};

export const filtersSlice = createSlice({
  initialState: initialState(),
  name: 'filters',
  reducers: {
    clearFilters: (state) => {
      state.pageFilter = 'current';
      state.notifiedUsers = undefined;
      state.orderBy = undefined;
      state.taskStatus = undefined;
      state.taskTypes = undefined;
      state.textSearch = undefined;
      // Clear localStorage when clearing filters
      updateFilterState({
        pageFilter: 'current',
        notifiedUsers: undefined,
        orderBy: undefined,
        taskStatus: undefined,
        taskTypes: undefined,
        textSearch: undefined,
      });
    },
    setPageFilter: (state, action: PayloadAction<PageFilterType>) => {
      state.pageFilter = action.payload;
      updateFilterState({ pageFilter: action.payload });
    },
    setNotifiedUsers: (state, action: PayloadAction<string | undefined>) => {
      state.notifiedUsers = action.payload;
      updateFilterState({ notifiedUsers: action.payload });
    },
    setOrderBy: (
      state,
      action: PayloadAction<
        'created_at' | 'task_priority' | 'is_unread' | 'task_status' | undefined
      >
    ) => {
      state.orderBy = action.payload;
      updateFilterState({ orderBy: action.payload });
    },
    // Updated to handle the new complete task logic
    setTaskStatus: (
      state,
      action: PayloadAction<TaskStatus | TaskStatus[] | undefined>
    ) => {
      state.taskStatus = action.payload;
      updateFilterState({ taskStatus: action.payload });
    },
    // Updated to support more task types and arrays
    setTaskTypes: (
      state,
      action: PayloadAction<TaskType | TaskType[] | undefined>
    ) => {
      state.taskTypes = action.payload;
      updateFilterState({ taskTypes: action.payload });
    },
    setTextSearch: (state, action: PayloadAction<string | undefined>) => {
      state.textSearch = action.payload;
      updateFilterState({ textSearch: action.payload });
    },
    // New action for toggling complete tasks specifically
    toggleCompleteTasksFilter: (state) => {
      // If currently showing complete tasks, remove them
      if (state.taskStatus === 'complete') {
        state.taskStatus = undefined;
      } else {
        // If not showing complete tasks, set to show complete
        state.taskStatus = 'complete';
      }
      updateFilterState({ taskStatus: state.taskStatus });
    },
    // New action for toggling internal tasks specifically
    toggleInternalTasksFilter: (state) => {
      // If currently showing internal tasks, remove them
      if (state.taskTypes === 'internal') {
        state.taskTypes = undefined;
      } else {
        // If not showing internal tasks, set to show internal
        state.taskTypes = 'internal';
      }
      updateFilterState({ taskTypes: state.taskTypes });
    },
    // New action for setting multiple task statuses
    setMultipleTaskStatuses: (
      state,
      action: PayloadAction<TaskStatus[] | undefined>
    ) => {
      state.taskStatus = action.payload;
      updateFilterState({ taskStatus: action.payload });
    },
    // New action for setting multiple task types
    setMultipleTaskTypes: (
      state,
      action: PayloadAction<TaskType[] | undefined>
    ) => {
      state.taskTypes = action.payload;
      updateFilterState({ taskTypes: action.payload });
    },
  },
});

export const {
  clearFilters,
  setNotifiedUsers,
  setOrderBy,
  setTaskStatus,
  setTaskTypes,
  setTextSearch,
  setPageFilter,
  toggleCompleteTasksFilter,
  toggleInternalTasksFilter,
  setMultipleTaskStatuses,
  setMultipleTaskTypes,
} = filtersSlice.actions;

// Existing selectors
export const getCurrentPageUrlFilter = (state: RootState) =>
  state.filters.pageFilter;
export const getNotifiedUsersFilter = (state: RootState) =>
  state.filters.notifiedUsers;
export const getOrderByFilter = (state: RootState) => state.filters.orderBy;
export const getTaskStatusFilter = (state: RootState) =>
  state.filters.taskStatus;
export const getTaskTypesFilter = (state: RootState) => state.filters.taskTypes;
export const getTextSearchFilter = (state: RootState) =>
  state.filters.textSearch;
export const getPageFilter = (state: RootState) => state.filters.pageFilter;

// New helper selectors for better type safety and logic
export const getIsCompleteTasksFilterActive = (state: RootState): boolean => {
  const taskStatus = state.filters.taskStatus;
  if (Array.isArray(taskStatus)) {
    return taskStatus.includes('complete');
  }
  return taskStatus === 'complete';
};

export const getIsInternalTasksFilterActive = (state: RootState): boolean => {
  const taskTypes = state.filters.taskTypes;
  if (Array.isArray(taskTypes)) {
    return taskTypes.includes('internal');
  }
  return taskTypes === 'internal';
};

// Helper selector to get all active task statuses
export const getActiveTaskStatuses = (state: RootState): TaskStatus[] => {
  const taskStatus = state.filters.taskStatus;
  if (Array.isArray(taskStatus)) {
    return taskStatus;
  }
  if (taskStatus) {
    return [taskStatus];
  }
  // Return default statuses when no filter is applied
  return ['in-progress', 'open', 'pending-review'];
};

// Helper selector to get all active task types
export const getActiveTaskTypes = (
  state: RootState
): TaskType[] | undefined => {
  const taskTypes = state.filters.taskTypes;
  if (Array.isArray(taskTypes)) {
    return taskTypes;
  }
  if (taskTypes) {
    return [taskTypes];
  }
  return undefined;
};

export default filtersSlice.reducer;
